import type { Permission, ScopeAssignment, SecurityAccount } from '@alola/contracts';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  apiRequest,
  refreshSession,
  setAccessToken,
  setSessionEndedListener,
} from './client';

/**
 * Who is signed in, and what they may see.
 *
 * `permissions` is used **only** to decide what to render. Every one of them is enforced again on the
 * server, inside the query (ADR-0006); hiding a menu item is a courtesy to the person using the
 * product, never a control. A test in the E2E suite calls a hidden endpoint directly and asserts the
 * API refuses it, which is what makes that claim checkable rather than a comment.
 */
export interface Session {
  account: SecurityAccount;
  permissions: Permission[];
  scope: ScopeAssignment;
  sessionId?: string;
}

export type SessionStatus = 'loading' | 'signedOut' | 'signedIn';

interface SessionContextValue {
  status: SessionStatus;
  session: Session | undefined;
  /** Password only (ADR-0046). */
  signIn: (loginIdentifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
  canAny: (permissions: readonly Permission[]) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface LoginResponse {
  status: 'authenticated';
  accessToken: string;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<Session | undefined>();

  const loadSession = useCallback(async () => {
    const loaded = await apiRequest<Session>('/api/v1/me');
    setSession(loaded);
    setStatus('signedIn');
  }, []);

  /**
   * On first paint, try to restore the session from the refresh cookie.
   *
   * A failure here is the ordinary case — nobody is signed in — so it resolves to `signedOut` rather
   * than surfacing an error. Only a real sign-in attempt reports why it failed.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Single-flight: StrictMode runs this effect twice in development, and both runs must share
        // one refresh — a second request with the same cookie is a replay to the server (SEC-015).
        const token = await refreshSession();
        if (!token) throw new ApiError(401, 'UNAUTHENTICATED');
        if (!cancelled) await loadSession();
      } catch {
        if (!cancelled) {
          setAccessToken(undefined);
          setStatus('signedOut');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  // The client tells us when a refresh failed mid-flight, so the shell can show the sign-in screen
  // rather than a page of empty tables.
  useEffect(() => {
    setSessionEndedListener(() => {
      setSession(undefined);
      setStatus('signedOut');
    });
    return () => setSessionEndedListener(undefined);
  }, []);

  const signIn = useCallback(
    async (loginIdentifier: string, password: string) => {
      const result = await apiRequest<LoginResponse>('/api/v1/auth/login', {
        method: 'POST',
        body: { loginIdentifier, password },
        skipRefresh: true,
      });
      setAccessToken(result.accessToken);
      await loadSession();
    },
    [loadSession],
  );

  const signOut = useCallback(async () => {
    try {
      await apiRequest('/api/v1/auth/logout', { method: 'POST' });
    } finally {
      // Whatever the server said, this browser forgets the token.
      setAccessToken(undefined);
      setSession(undefined);
      setStatus('signedOut');
    }
  }, []);

  const value = useMemo<SessionContextValue>(() => {
    const held = new Set(session?.permissions ?? []);
    return {
      status,
      session,
      signIn,
      signOut,
      can: (permission) => held.has(permission),
      canAny: (permissions) => permissions.some((permission) => held.has(permission)),
    };
  }, [session, signIn, signOut, status]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionContextValue {
  const value = use(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
